<script lang="ts">
  import { afterNavigate } from "$app/navigation";

  import { Hamburger } from "svelte-hamburgers";

  import lightCursor from "$lib/assets/marketing/gpu-glossary/light-cursor.svg";
  import lightGreenCursor from "$lib/assets/marketing/gpu-glossary/light-green-cursor.svg";
  import lightGreenPointerCursor from "$lib/assets/marketing/gpu-glossary/light-green-pointer-cursor.svg";
  import lightGreenSelectionCursor from "$lib/assets/marketing/gpu-glossary/light-green-selection-cursor.svg";
  import lightPointerCursor from "$lib/assets/marketing/gpu-glossary/light-pointer-cursor.svg";
  import lightSelectionCursor from "$lib/assets/marketing/gpu-glossary/light-selection-cursor.svg";
  import logoLightGreen from "$lib/assets/marketing/gpu-glossary/modal-logo-light-green.svg";
  import logoLight from "$lib/assets/marketing/gpu-glossary/modal-logo-light.svg";
  import logoTerminal from "$lib/assets/marketing/gpu-glossary/modal-logo-terminal.svg";
  import terminalCursor from "$lib/assets/marketing/gpu-glossary/terminal-cursor.svg";
  import terminalPointerCursor from "$lib/assets/marketing/gpu-glossary/terminal-pointer-cursor.svg";
  import terminalSelectionCursor from "$lib/assets/marketing/gpu-glossary/terminal-selection-cursor.svg";
  import type { ThemeName } from "$lib/gpu-glossary";
  import { themeStore } from "$lib/gpu-glossary";
  import { isGlossaryMobileMenuOpen } from "$lib/gpu-glossary";
  import Contents from "$lib/ui/gpu-glossary/Contents.svelte";
  import GlossarySearch from "$lib/ui/gpu-glossary/GlossarySearch.svelte";
  import SignUpButton from "$lib/ui/gpu-glossary/SignUpButton.svelte";
  import ThemeOptions from "$lib/ui/gpu-glossary/ThemeOptions.svelte";
  import contents from "./contents.json";

  let scrollContainer: HTMLDivElement;

  const cursorMap: Record<ThemeName, string> = {
    lightGreen: lightGreenCursor,
    terminal: terminalCursor,
    light: lightCursor,
  };

  const pointerCursorMap: Record<ThemeName, string> = {
    lightGreen: lightGreenPointerCursor,
    terminal: terminalPointerCursor,
    light: lightPointerCursor,
  };

  const selectionCursorMap: Record<ThemeName, string> = {
    lightGreen: lightGreenSelectionCursor,
    terminal: terminalSelectionCursor,
    light: lightSelectionCursor,
  };

  $: cursorUrl = cursorMap[$themeStore.name];
  $: pointerCursorUrl = pointerCursorMap[$themeStore.name];
  $: selectionCursorUrl = selectionCursorMap[$themeStore.name];

  afterNavigate(() => {
    scrollContainer.scrollTop = 0;
  });
  $: style = `
  --cursor-url: url("${cursorUrl}");
  --pointer-cursor-url: url("${pointerCursorUrl}");
  --selection-color: ${$themeStore.primary[20]};
  --selection-cursor-url: url("${selectionCursorUrl}");
  `;
</script>

<svelte:head>
  <link rel="stylesheet" href="https://use.typekit.net/psj2ndn.css" />
</svelte:head>

<main
  class="flex w-screen h-screen md:p-4 custom-scrollbar"
  style:background-color={$themeStore.secondary[100]}
  style:color={$themeStore.primary[100]}
  {style}
>
  <div
    class="flex flex-col glossary-font-primary border w-full custom-cursor"
    style:border-color={$themeStore.primary[100]}
    style:background-color={$themeStore.primary[5]}
  >
    <div
      class="md:grid md:grid-cols-4 border-b flex justify-between items-center pl-4 md:pl-0"
      style:border-color={$themeStore.primary[100]}
    >
      <a
        class="flex items-center gap-x-2 text-3xl leading-6 md:p-4 p-2 -ml-3 md:-ml-0"
        href="/"
        style:color={$themeStore.primary[100]}
      >
        {#if $themeStore.name === "light"}
          <img src={logoLight} alt="GPU Glossary" />
        {:else if $themeStore.name === "lightGreen"}
          <img src={logoLightGreen} alt="GPU Glossary" />
        {:else}
          <img src={logoTerminal} alt="GPU Glossary" />
        {/if}
        <div
          class="glossary-font-secondary flex md:hidden col-span-2 items-center md:ml-14 text-xl"
        >
          GPU Glossary
        </div>
      </a>
      <div
        class="glossary-font-secondary hidden md:flex col-span-2 items-center md:ml-14 text-2xl"
      >
        GPU Glossary
      </div>
      <div
        class="hidden md:flex justify-end glossary-font-secondary gap-x-4 items-center p-4 text-sm"
      >
        <div class="flex lg:flex md:hidden gap-x-4 flex-shrink-0">
          <ThemeOptions />
        </div>
        <div class="pl-3 flex items-center gap-x-6 flex-shrink-0">
          <GlossarySearch />
          <SignUpButton />
        </div>
      </div>
      <div
        class="md:hidden self-end py-4 px-2 md:p-4 flex gap-x-2 items-center"
      >
        <div class="block md:hidden">
          <GlossarySearch />
        </div>
        <Hamburger
          bind:open={$isGlossaryMobileMenuOpen}
          --color={$themeStore.primary[100]}
          --layer-height="1.5px"
          --layer-width="18px"
          --layer-spacing="5px"
          --padding="4px"
        />
      </div>
    </div>
    {#if $isGlossaryMobileMenuOpen}
      <Contents {contents} showAdditionalControls={true} />
    {/if}
    <div
      class="grid grid-cols-4"
      style="height: calc(100% - 56px);"
      style:display={$isGlossaryMobileMenuOpen ? "none" : "grid"}
    >
      <div
        class="md:block hidden overflow-y-auto background-styles"
        style:background-image={`linear-gradient(to bottom, ${$themeStore.primary[100]} 50%, transparent 50%)`}
        style:background-size="1px 12px"
        style:background-position="right"
        style:background-repeat="repeat-y"
        style:height="calc(100% - 5px)"
      >
        <Contents {contents} showAdditionalControls={false} />
      </div>
      <div
        class="md:col-span-3 overflow-y-auto col-span-4 relative"
        style:background-color={$themeStore.secondary[100]}
        style="height: calc(100% - 5px)"
        bind:this={scrollContainer}
      >
        <slot />
      </div>
    </div>
  </div>
</main>

<style>
  main :global(.glossary-font-primary) {
    font-family: "degular-mono", sans-serif;
  }
  main :global(.glossary-font-secondary) {
    font-family: "cofo-sans-pixel", sans-serif;
  }

  main {
    cursor:
      var(--cursor-url) 12 12,
      auto;
  }

  main :global([style*="cursor: pointer"]),
  main :global(a),
  main :global(button) {
    cursor: var(--pointer-cursor-url), pointer;
  }

  @media (max-width: 640px) {
    .background-styles {
      background-image: none !important;
    }
  }
  main :global(::selection) {
    background-color: var(--selection-color);
  }

  main:active {
    cursor: var(--selection-cursor-url), text;
  }

  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: var(--selection-color) transparent;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    width: 4px;
    height: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: var(--selection-color);
    border-radius: 4px;
    height: 20px;
  }

  .custom-scrollbar:hover::-webkit-scrollbar-thumb {
    background-color: var(--selection-color);
  }

  .custom-scrollbar:not(:hover):not(:active)::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
</style>
